/* eslint-disable prefer-destructuring */
import { store } from '@redux/store'
import _get from 'lodash/get'
import { flatten, uniq } from 'lodash'
import {
  setOrder,
  setDeliveryCalendar as setDeliveryCalendarRedux,
  setDeliveryInvalidFields,
  setDeliveryMode,
} from '@redux/modules/checkout'
import {
  PICK_UP,
  DOORWAY_DELIVERY,
  PROFESSIONAL_DELIVERY,
} from '@components/checkout/checkout-parts/delivery-section/constants'
import { getFromBrowserStorage } from '@helpers/storage'
import { updateDelivery } from '@services/checkout'
import { getStore, getStoreHours } from '../store-locator'
import { abbreviatedMonths, months, weekdays, getStandardTime } from '../string-helper'
import { getDateFull } from '../date'
import { getDeliveryMode, getOrder } from './global'

export const getDistributionCenter = order => {
  const orderStore = getStore(order)
  if (orderStore && orderStore.pickup_hours) {
    return {
      distributionCenter: {
        name: `${orderStore.city} Distribution Center`,
        hours: getStoreHours(orderStore.pickup_hours),
      },
    }
  }
  return {}
}

export const getDeliverySliderIndex = (selectedDate, dates) => {
  let index = 0
  if (dates.filter(date => date.isExpressDelivery).length > 0) {
    if (dates.filter(date => date.isStandardDelivery).findIndex(date => date.readable === selectedDate.readable) > 1) {
      index = dates.filter(date => date.isStandardDelivery).findIndex(date => date.readable === selectedDate.readable)
    }
  }
  return index
}

const formatDate = date => {
  const mon = abbreviatedMonths[date.getMonth()]
  const day = date
    .getDate()
    .toString()
    .padStart(2, '0') // eslint-disable-line
  const fullDate = `${weekdays?.[date.getDay()]}, ${months[date.getMonth()]} ${date
    .getDate()
    .toString()
    .padStart(2, '0')}, ${date.getFullYear()}`

  return { mon, day, fullDate }
}

/**
 *
 * @param {string} date in the format "YYYY-MM-DD"
 * @returns object containing:
 * deliverDate: date converted to Date object,
 * fortNight: Date object with value of deliverDate + 14 days, and
 * splitDatesRange: readable text showing the range from deliverDate to fortNight
 * splitDatesRangeFull: readable text showing the full date range from deliverDate to fortNight
 */
export const splitDates = date => {
  const [yyyy, mm, dd] = date.split('-').map(m => parseInt(m))

  const deliverDate = new Date()
  deliverDate.setFullYear(yyyy)
  deliverDate.setMonth(mm - 1)
  deliverDate.setDate(dd)

  const fortNight = new Date(deliverDate)
  fortNight.setDate(dd + 14)

  const splitDatesRange = `${months[deliverDate.getMonth()]} ${deliverDate.getDate()} - ${
    months[fortNight.getMonth()]
  } ${fortNight.getDate()}`

  const firstDate = formatDate(deliverDate).fullDate
  const lastDate = formatDate(fortNight).fullDate
  const splitDatesRangeFull = `${firstDate} - ${lastDate}`

  return { deliverDate, fortNight, splitDatesRange, splitDatesRangeFull }
}

/**
 *
 * @param {object} order the order object
 * @param {string} deliveryMode
 * @param {boolean} [isSettingDeliveryMode] optional boolean that indicates function being called to update the mode
 * @returns {object} request body for updateDelivery call
 */
export const getDeliverySpecificBody = (order = {}, deliveryMode, isSettingDeliveryMode = false) => {
  let deliveryType = 'U'
  const {
    additionalDirections,
    deliveryDate: orderDeliveryDate,
    lineItems = [],
    orderId,
    splitCalendar,
    splitDelivery,
    splitDeliveryDates,
  } = order

  let deliveryDate = orderDeliveryDate
  const storeCartLineItems = lineItems.filter(lineItem => lineItem.isStoreSku)
  const onlineCartLineItems = lineItems.filter(lineItem => !lineItem.isStoreSku)
  const onlineCartIsOnlyNonRTGItems = !onlineCartLineItems.some(item => ['D', 'K'].includes(item.deliveryType))

  if (storeCartLineItems.length > 0 && onlineCartIsOnlyNonRTGItems) {
    // only storeCartItems
    const storeCartDeliveryDates = storeCartLineItems.map(i => i.deliveryDate).sort()
    const earliestStoreCartDate = storeCartDeliveryDates.length ? storeCartDeliveryDates[0] : null
    const latestStoreCartDate = storeCartDeliveryDates.length
      ? storeCartDeliveryDates[storeCartDeliveryDates.length - 1]
      : null

    deliveryType = storeCartLineItems[0].deliveryType
    if (!deliveryMode || isSettingDeliveryMode) {
      deliveryDate = splitCalendar ? latestStoreCartDate || deliveryDate : earliestStoreCartDate || deliveryDate // ensure delivery date assigned to Store Cart is used
    }
  }

  if (lineItems.some(item => ['D', 'K'].includes(item.deliveryType))) {
    deliveryType = 'D'
  }

  if (lineItems.some(item => ['S'].includes(item.deliveryType))) {
    deliveryType = 'S'
  }

  const body = { additionalDirections, deliveryType, orderId, shouldCombineUPSWithTruck: true }

  // https://github.com/RoomstoGoDigital/roomstogatsby/pull/4527
  // This fixes FD-2419. The issue was that the deliveryMode had the value of PROFESSIONAL_DELIVERY no matter what initial value the order has
  // If the user hadn't yet visited delivery section, a toogleDefaultDeliveryMode() would set the deliveryMode to PROFESSIONAL_DELIVERY
  // This code checks preselected deliveryMode by checking if the order has a value for isPickup or doorwayDeliver by:
  // deliveryMode === PROFESSIONAL_DELIVERY && !!order?.isPickup -> On first render deliveryMode is PROFESSIONAL_DELIVERY so we need to check if the order has a value for isPickup
  // and then the || deliveryMode === PICK_UP makes sure to also change it if the user is changing the deliveryMode manually
  // isPickup: deliveryMode ? deliveryMode === PICK_UP : !!order?.isPickup,
  // doorwayDelivery: deliveryMode ? deliveryMode === DOORWAY_DELIVERY : !!order?.doorwayDelivery,
  if (deliveryMode && !isSettingDeliveryMode) {
    body.isPickup = (deliveryMode === PROFESSIONAL_DELIVERY && !!order?.isPickup) || deliveryMode === PICK_UP
    body.doorwayDelivery =
      (deliveryMode === PROFESSIONAL_DELIVERY && !!order?.doorwayDelivery) || deliveryMode === DOORWAY_DELIVERY
  } else {
    body.isPickup = !!order?.isPickup
    body.doorwayDelivery = !!order?.doorwayDelivery
  }

  if (deliveryType === 'D' || deliveryType === 'S') {
    body.deliveryDate = deliveryDate || (order?.deliveryCalendar?.[0] ?? '')
  }
  if (deliveryType === 'P') {
    body.deliveryDate = deliveryDate || (order?.pickupCalendar?.[0] ?? '')
  }
  if (deliveryType === 'E') body.deliveryDate = deliveryDate || _get(order, 'expressCalendar.0', '')

  if (splitDelivery) {
    body.splitDelivery = splitDelivery
  }

  if (!splitDelivery && splitDeliveryDates) {
    body.splitDelivery = splitDeliveryDates.map(({ deliveryDate: delDate }) => delDate)
  }

  return body
}

export const setDeliveryInfo = (info, field) => {
  let order = getOrder()
  order = {
    ...order,
    [field]: info,
  }
  store.dispatch(setOrder(order))
}

export const setDeliveryCalendar = (deliveryCalendar, pickupCalendar, expressCalendar = [], setCalendar = true) => {
  const calendar =
    expressCalendar?.map(expressDate => {
      const [dayOfWeek, mon, dd] = new Date(`${expressDate}T06:00:00`).toDateString().split(' ')
      const month = months.find(m => m.startsWith(mon))
      return {
        date: expressDate,
        readable: `${month} ${dd}`,
        dayOfWeek,
        isPickup: false,
        isStandardDelivery: true,
        isExpressDelivery: true,
      }
    }) ?? []

  if (deliveryCalendar?.length) {
    deliveryCalendar.forEach(delDate => {
      const [dayOfWeek, mon, dd] = new Date(`${delDate}T06:00:00`).toDateString().split(' ')
      const month = months.find(m => m.startsWith(mon))

      calendar.push({
        date: delDate,
        readable: `${month} ${dd}`,
        dayOfWeek,
        isPickup: false,
        isStandardDelivery: true,
        isExpressDelivery: false,
      })
    })
  }

  if (pickupCalendar?.length) {
    pickupCalendar.forEach(pickupDate => {
      const index = calendar.findIndex(calendarDate => calendarDate.date === pickupDate)
      if (index !== -1) {
        calendar[index].isPickup = true
      } else {
        const [dayOfWeek, mon, dd] = new Date(`${pickupDate}T06:00:00`).toDateString().split(' ')
        const month = months.find(m => m.startsWith(mon))

        calendar.push({
          date: pickupDate,
          readable: `${month} ${dd}`,
          dayOfWeek,
          isPickup: true,
          isStandardDelivery: false,
          isExpressDelivery: false,
        })
      }
    })
  }

  calendar.sort((a, b) => {
    if (a.date < b.date) {
      return -1
    }
    if (a.date > b.date) {
      return 1
    }
    return 0
  })
  if (setCalendar) store.dispatch(setDeliveryCalendarRedux(calendar))
  return calendar
}

export const getRoomsToGoDeliveryItems = lineItems =>
  lineItems?.filter(
    ({ deliveryType, sku }) =>
      ['D', 'E', 'K', 'P'].includes(deliveryType) || (deliveryType === 'S' && sku !== '99999925'),
  ) ?? []

export const getNonRTGDeliveryItems = lineItems => {
  const getDelItems = type => lineItems?.filter(lineItem => lineItem?.deliveryType === type) ?? []

  return {
    ups: getDelItems('U'),
    usps: getDelItems('T'),
    vendor: getDelItems('O'),
  }
}

export const getDeliveryDate = (order, deliveryCalendar, pickup = false) => {
  if (order && deliveryCalendar) {
    let deliveryDate
    if (order.deliveryDate) {
      if ((order.isPickup && pickup) || (!order.isPickup && !pickup)) {
        deliveryDate = deliveryCalendar.filter(date => date.date === order.deliveryDate)[0]
      } else if (!order.isPickup && pickup) {
        deliveryDate = deliveryCalendar.filter(date => date.isPickup)[0]
      } else {
        deliveryDate = deliveryCalendar.filter(date => date.isStandardDelivery && !date.isExpressDelivery)[0]
      }
    } else if (pickup) {
      deliveryDate = deliveryCalendar.filter(date => date.isPickup)[0]
    } else {
      deliveryDate = deliveryCalendar.filter(date => date.isStandardDelivery && !date.isExpressDelivery)[0]
    }
    if (deliveryCalendar.length > 0 && deliveryDate) {
      let storedDate
      let expressDate
      if (order.isPickup) {
        storedDate = deliveryCalendar.filter(date => date.isPickup)[0]
      } else {
        const expressDates = deliveryCalendar.filter(date => date.isExpressDelivery)
        expressDate = expressDates.length > 0 ? expressDates[0] : null
        storedDate = deliveryCalendar.filter(date => date.isStandardDelivery && !date.isExpressDelivery)[0]
      }

      const selectedDate = deliveryDate.date
      if (deliveryDate.readable !== '' && selectedDate >= ((expressDate && expressDate.date) || storedDate.date)) {
        return {
          ...deliveryDate,
        }
      }
      return {
        ...deliveryDate,
      }
    }
    return {
      readable: 'No delivery dates avaliable.',
      date: new Date().toISOString().split('T')[0],
    }
  }
  return {}
}

export const getDistributionCenterHours = (order, date) => {
  const thisDate = new Date(date.date).setHours(new Date(date.date).getHours() + 6)
  const day = weekdays[new Date(thisDate).getDay()]
  if (order) {
    const location = getStore(order)
    if (location && day) {
      const { pickup_hours } = location
      const dayHours = {
        openTime: pickup_hours[`${day.toLowerCase()}Open`],
        closeTime: pickup_hours[`${day.toLowerCase()}Closed`],
      }
      if (dayHours && dayHours.openTime)
        return `${getStandardTime(dayHours.openTime)}-${getStandardTime(dayHours.closeTime)}`.toUpperCase()
    }
  }
  return ''
}

export const getDistributionDaysClosed = order => {
  if (order) {
    const daysClosed = []
    for (let i = 0, n = weekdays.length; i < n; i++) {
      const orderStore = getStore(order)
      if (orderStore) {
        const { pickup_hours } = orderStore
        if (pickup_hours && !pickup_hours[`${weekdays[i].toLowerCase()}Open`]) {
          daysClosed.push(weekdays[i].substr(0, 3))
        }
      }
    }
    if (daysClosed.length === 1) {
      return daysClosed[0]
    }
    if (daysClosed.length === 2) {
      return `${daysClosed[0]} and ${daysClosed[1]}`
    }
    let str = ''
    for (let i = 0, n = daysClosed.length; i < n; i++) {
      if (i === n - 1) str = `${str}and ${daysClosed[i]}`
      else str = `${str}${daysClosed[i]}, `
    }
    return str
  }
  return ''
}

const getStoreCartDeliveryDate = storeCart =>
  storeCart?.lineItems?.length ? storeCart.lineItems[0]?.deliveryDate : null

export const getDeliverySectionData = (orderProp, deliveryCalendar) => {
  const order = orderProp ? { ...orderProp } : getOrder()
  const rtgDeliveryItems = getRoomsToGoDeliveryItems(order?.lineItems ?? [])
  const nonRTGDeliveryItems = getNonRTGDeliveryItems(order?.lineItems ?? [])

  // deliveryDate is 'YYYY-MM-DD' string
  const deliveryDate =
    (order?.deliveryDate ||
      (order?.storeCart && getStoreCartDeliveryDate(order.storeCart)) ||
      deliveryCalendar?.find(date => date.isStandardDelivery && !date.isExpressDelivery)?.date) ??
    undefined

  const isExpress =
    deliveryCalendar?.length > 0 &&
    deliveryDate &&
    `${deliveryDate}` &&
    deliveryCalendar?.some(day => day.isExpressDelivery) &&
    deliveryCalendar?.filter(day => day.isExpressDelivery && day.date === deliveryDate).length === 1

  const fullDeliveryDate = deliveryDate ? getDateFull(deliveryDate) : undefined

  const defaultDeliveryInterval = order.defaultDelivery ? splitDates(order.deliveryDate).splitDatesRangeFull : ''

  return { defaultDeliveryInterval, fullDeliveryDate, isExpress, nonRTGDeliveryItems, rtgDeliveryItems }
}

// eslint-disable-next-line
export let validateDeliveryStep = async nextStep => {
  let invalidFields = []
  const order = getOrder()
  const deliveryMode = getDeliveryMode()

  await updateDelivery(getDeliverySpecificBody(order, deliveryMode))
    .then(data => {
      store.dispatch(setOrder(data))
    })
    .catch(err => {
      store.dispatch(setDeliveryMode())
      console.error('Error updating delivery mode', err)
    })
  if (nextStep === 'review') {
    invalidFields = ['payment incomplete']
  }
  store.dispatch(setDeliveryInvalidFields(invalidFields))
  return invalidFields
}

export const canSkipDelivery = async () => {
  const order = getOrder()
  const rtgDeliveryItems = getRoomsToGoDeliveryItems(order?.lineItems)
  if (rtgDeliveryItems?.length < 1 && !getFromBrowserStorage('session', 'storecart')) {
    await validateDeliveryStep('payment')
    return true
  }
  return false
}

// function used in previous version of Checkout Delivery to return an array
// of delivery date objects. No longer used, but placed here just in case
export const getDeliveryDates = (isPickup, deliveryCalendar) => {
  const dateFormatter = dateString => {
    const newDate = new Date(dateString)
    return {
      date: dateString,
      readable: `${months[newDate.getUTCMonth()]} ${newDate.getUTCDate()}`,
      dayOfWeek: weekdays[newDate.getUTCDay()].substring(0, 3),
    }
  }

  if (!isPickup) {
    /* premium delivery or doorway delivery dates with potential express dates prepended to the front of a new array */
    if (deliveryCalendar?.expressDeliveryDates?.length > 0) {
      return [
        ...deliveryCalendar?.expressDeliveryDates
          ?.map(dateFormatter)
          ?.map(date => ({ ...date, isExpressDelivery: true })),
        ...deliveryCalendar?.deliveryDates?.map(dateFormatter),
      ]
    }
    return deliveryCalendar?.deliveryDates?.map(dateFormatter)
  }

  /* pickup delivery dates */
  return deliveryCalendar?.pickupDates?.map(dateFormatter)
}

export const getSplitShipmentData = (cartItems = [], order = {}) => {
  const selectionsItems = [...(cartItems?.map(item => item?.selections) || [])]
  const shipmentsData = order.splitCalendar.map((shipment, index) => ({
    expressDelivery: order?.splitDeliveryDates
      ? !order.isPickup && order.splitDeliveryDates[index]?.expressDelivery
      : false,
    data: shipment,
    deliveryDate: order?.splitDeliveryDates?.[index]?.deliveryDate ?? '',
    deliveryDateObject: new Date(order?.splitDeliveryDates?.[index]?.deliveryDate),
    items: uniq(
      flatten(
        shipment.skus.map(sku =>
          [
            ...cartItems,
            ...(order?.lineItems
              ?.map(item => {
                if (!item?.product) {
                  const selectedItemProduct = selectionsItems?.find(selectionItem => selectionItem?.sku === item?.sku)
                  item.product = selectedItemProduct
                  item.isSelectionItem = !!item.product
                }
                return item
              })
              .filter(x => x.isStoreSku || x.isSelectionItem) ?? []),
          ].filter(
            item =>
              item.sku === sku ||
              item?.product?.sku === sku ||
              item?.product?.items_in_room?.map(subItem => subItem.sku).includes(sku) ||
              item?.product?.items_in_room?.map(subItem => subItem.sku).includes(order?.packageSkuMap?.[sku]) ||
              item?.activeAddons?.map(activeAddon => activeAddon.sku).includes(sku) ||
              order.lineItems.filter(lineItem => {
                if (lineItem.sku === item.sku) {
                  item.subItems = lineItem.childItems
                  return lineItem.childItems.map(childItem => childItem.sku).includes(sku)
                }

                return false
              }).length > 0,
          ),
        ),
      ),
    ),
  }))
  return shipmentsData
}
